import { CellRange, FlexGrid, CellType, Column, Row, CellRangeEventArgs, ColumnGroupCollection, AllowSorting } from '@grapecity/wijmo.grid';
import { SortDescription, toggleClass } from "@grapecity/wijmo";

import { NpFlexGridFreezeBar } from './np-flex-grid-freeze-bar';

import { md5 } from '../utils/md5';
import { NpFlexGridUtils } from './np-flex-grid-utils';

import { NpFlexGridSelector2 } from './np-flex-grid-selector2';

import * as wjcGrid from '@grapecity/wijmo.grid';
import * as wjcCore from '@grapecity/wijmo';
import { NpServerCollectionView } from '../collection-view/np-scv/np-server-collection-view';

/**
 * NpFlexGridCtrl Options
 */
export declare type NpFlexGridCtrlOpts = {
    // debug - 개발을 위한 여러 로그를 콘솔로 출력한다.
    debug?: boolean;
    // Selector 동작 방식
    selector?: 'NONE' | 'SINGLE' | 'MULTI';
    // 컬럼레이아웃 아이디
    flexGridId?: string;
    // 엑셀다운로드 파일명
    excelDownload?: boolean,
    excelFileName?: string;
    sortWithAutoSize?: boolean,
    excelDownloadFn?: (flexGrid: FlexGrid) => void;

    /**
     *  최초 그리드 로딩 시 첫번째 Cell 자동선택 여부
     *  @deprecated
    */
    selectFirstCell?: boolean;
}

declare type GridColumnStateConfig = {
    frozenColumns: number,
    columnsbindingKey: string,
    columnsLength: number
    columnsState: any[]
}

declare type NpGridEventState = {
    sortingColIdx?: number;
}

export class NpFlexGridCtrl {

    _np_gridEventState: NpGridEventState = {};

    private _defaultConfig!: GridColumnStateConfig;
    private _defaultColumns!: any[];

    ///////////////////////////////////////////// FLEX GRID STATE CONFIG --- START
    private makeColumnsBindkey() {
        const bindingKeyStr = this._defaultColumns.map(v => v.binding).join('|');
        return md5(bindingKeyStr);
    }
    private makeGridStateConfig() {

        let columnsSetting: any = [];
        this.flexGrid.columns.forEach(v => {
            columnsSetting.push({
                idx: this._defaultColumns.indexOf(v),
                width: v.width,
                visible: v.visible
            })
        })
        let gridStateConfig: GridColumnStateConfig = {
            frozenColumns: this.flexGrid.frozenColumns,
            columnsbindingKey: this.makeColumnsBindkey(),
            columnsLength: this._defaultColumns.length,
            columnsState: columnsSetting
        };
        return gridStateConfig;
    }
    private applyGridStateConfig(gridConfig: GridColumnStateConfig) {
        const columnsbindingKey = this.makeColumnsBindkey()
        if (columnsbindingKey !== gridConfig.columnsbindingKey || this._defaultColumns.length !== gridConfig.columnsLength) {
            this.deleteUserStateConfig();
        } else {
            const cs = gridConfig.columnsState;
            cs.forEach((v, idx) => {
                const target = this._defaultColumns[v.idx];
                if (v.width) {
                    target.width = v.width
                }
                target.visible = v.visible;
                const cIdx = this.flexGrid.columns.indexOf(target);
                this.flexGrid.columns.moveElement(cIdx, idx);
            })
            this.flexGrid.frozenColumns = gridConfig.frozenColumns;
        }
    }
    public resetGridState() {
        this.applyGridStateConfig(this._defaultConfig);
    }
    ///////////////////////////////////////////// FLEX GRID STATE CONFIG --- END

    /////////////////////////////////////////////  USER STATE CONFIG --- START
    public get canUserStateConfig() {
        return this.options.flexGridId ? true : false;
    }
    private get userStateConfigSaveKey() {
        if (this.canUserStateConfig) {
            return "flexgrid-config_" + this.options.flexGridId;
        } else {
            return null;
        }
    }
    private _loadUserStateConfig(): GridColumnStateConfig | null {
        if (!this.userStateConfigSaveKey) {
            return null;
        }
        const userStateConfigStr = localStorage.getItem(this.userStateConfigSaveKey);

        if (!userStateConfigStr) {
            return null;
        }
        return JSON.parse(userStateConfigStr)
    }
    private _saveUserStateConfig(gridStateConfig: GridColumnStateConfig) {
        if (!this.canUserStateConfig || !this.userStateConfigSaveKey) {
            alert("사용자 설정을 사용할 수 없습니다.");
            return;
        }
        localStorage.setItem(this.userStateConfigSaveKey, JSON.stringify(gridStateConfig));
    }

    public saveUserStateConfig() {
        const userConfig = this.makeGridStateConfig();
        this._saveUserStateConfig(userConfig);
    }

    public deleteUserStateConfig() {
        if (!this.userStateConfigSaveKey) {
            return;
        }
        localStorage.removeItem(this.userStateConfigSaveKey);
        this.resetGridState();
    }

    /////////////////////////////////////////////  USER STATE CONFIG --- END

    public get canExcelDownload() {
        return this.options.excelDownload
    }

    /**
     * excel download
     */
    saveToExcel() {

        if (this.options.excelDownloadFn) {
            this.options.excelDownloadFn(this.flexGrid);
            return;
        }

        if (!this.options.excelDownload) {
            return;
        }

        let filename = this.options.excelFileName || "FlexGrid-ExcelExport";
        let yyyyMMddHHmmss = wjcCore.Globalize.format(new Date(), 'yyyyMMddHHmmss');
        NpFlexGridUtils.saveToExcel(this.flexGrid, filename + '_' + yyyyMMddHHmmss, { ignoreFrozen: true });

        // if (!this._ctrlOpts.excelDownloadFn) {
        //     if (this._ctrlOpts.excelFileName) {
        //         let yyyyMMddHHmmss = wjcCore.Globalize.format(new Date(), 'yyyyMMddHHmmss');
        //         NpFlexGridUtils.saveToExcel(this.flexGrid, this._ctrlOpts.excelFileName + '_' + yyyyMMddHHmmss, { ignoreFrozen: true });
        //     }
        // } else {
        //     this._ctrlOpts.excelDownloadFn(this.flexGrid);
        // }
    }
    ///////////////////////////////////////////////////////////////////////

    private _DEBUG_HEADER = "NpFlexGridCtrl [DEBUG] - ";

    private debug(str: string, ...args: any[]) {
        if (this.options.debug !== true) {
            return;
        }
        if (args.length > 0) {
            for (const arg in args) {
                console.log(this._DEBUG_HEADER, str, args[arg]);
            }
        } else {
            console.log(this._DEBUG_HEADER, str);
        }

    }

    // default options
    private _options: NpFlexGridCtrlOpts = {
        debug: false,
        selector: 'NONE',
        excelDownload: false,
        sortWithAutoSize: true
    }

    public get options(): NpFlexGridCtrlOpts {
        return this._options
    }

    // flexGrid
    private _flexGrid!: FlexGrid;
    // Get flexGrid
    public get flexGrid(): FlexGrid {
        return this._flexGrid;
    }

    // 2024-11-11 selector2를 기본으로 사용한다.
    // selector2
    private _selector?: NpFlexGridSelector2;
    public get selector(): NpFlexGridSelector2 | null {
        if (this._selector) {
            return this._selector;
        } else {
            return null;
        }
    }

    // columnLayout
    // columnLayout: NpFlexGridColumnLayout;
    // freezBar
    freezBar!: NpFlexGridFreezeBar;

    /**
     * 클릭 이벤트
     */
    onClick?: (rowItem: any, cellData: any, row: wjcGrid.Row, column: wjcGrid.Column, htInfo: wjcGrid.HitTestInfo) => void;
    /**
     * 더블클릭 이벤트
     */
    onDoubleClick?: (rowItem: any, cellData: any, row: wjcGrid.Row, column: wjcGrid.Column, htInfo: wjcGrid.HitTestInfo) => void;

    /**
     * 셀렉터 상태 변경 이벤트
     */
    onSelectorStateChanged?: (selector: NpFlexGridSelector2) => void;

    /**
     * itemsSourceChanged 이벤트
     */
    // onItemsSourceChanged: (e?: EventArgs) => void;
    /**
     * 기본 init를 flexGridControl에서 사용하기 때문에 추가 초기화 내용 필요 시 등록
     */
    onInit?: (flexGrid: FlexGrid) => void;


    constructor(ctrlOpts?: NpFlexGridCtrlOpts) {
        this._options = { ...this._options, ...ctrlOpts };
        this.debug("constructor", this.options);
    }

    /**
     * FlexGrid 가 초기화 될 시 호출
     * @param flexGrid 
     */
    init(flexGrid: FlexGrid) {

        this.debug("init");

        // flexGrid 초기화 시 연결
        this._flexGrid = flexGrid;

        // 개발시 구성된 컬럼의 순서를 저장하는 어레이를 만든다.
        // 이때 deep clone과 같이 내부 객체가 복사 되는것이 아니라 내부 컬럼들은 참조가 연결되어 있어야 한다.
        this._defaultColumns = [...this.flexGrid.columns];
        // 개발 시 의도된 그리드 및 컬럼의 설정값을 저쟁해 놓는다.
        this._defaultConfig = this.makeGridStateConfig();

        if (this.canUserStateConfig) {
            // 사용자 설정을 로컬세션에서 불러온다.
            const userGridConfig = this._loadUserStateConfig();
            if (userGridConfig) {
                this.applyGridStateConfig(userGridConfig);
            }
        }

        switch (this.options.selector) {
            case 'SINGLE':
                this._selector = new NpFlexGridSelector2(this.flexGrid, false);
                if (this.onSelectorStateChanged) {
                    this._selector.onSelectorStateChanged = this.onSelectorStateChanged
                }
                break;
            case 'MULTI':
                this._selector = new NpFlexGridSelector2(this.flexGrid, true);
                if (this.onSelectorStateChanged) {
                    this._selector.onSelectorStateChanged = this.onSelectorStateChanged
                }
                break;
            case 'NONE':
                break;
            default:
                throw Error("please check selector options");
        }

        // 그리드 처음 초기화 시 실행
        this._onInitFlexGrid();

        // 추가로 초기화 할 내용 등록
        if (this.onInit) {
            this.onInit(this.flexGrid);
        }

    }

    // FlexGrid init의 초기화 연장 함수
    private _onInitFlexGrid() {

        this.debug("_init_flexgrid");

        // freezBar
        this.freezBar = new NpFlexGridFreezeBar(this.flexGrid);

        // selector
        if (this.selector) {

            let beforeRowIdx: number = -1;
            let currentRowIdx: number = -1;

            this.flexGrid.hostElement.addEventListener('click', (e) => {

                beforeRowIdx = currentRowIdx;
                currentRowIdx = this.flexGrid.selection.row;

                if (e.ctrlKey) {
                    if (beforeRowIdx > -1 && e.shiftKey && this.selector?.multiSelection === true) {

                        var htInfo = this.flexGrid.hitTest(e);
                        if (htInfo.cellType == wjcGrid.CellType.Cell) {
                            let rowIdxs = [beforeRowIdx, currentRowIdx].sort((a, b) => a - b);
                            const rows = this.flexGrid.rows.slice(rowIdxs[0], rowIdxs[1] + 1).filter(r => !(r instanceof wjcGrid.GroupRow));
                            if (rows.length > 0) {
                                if (rows.every((r: any) => this.selector?.isRowSelected(r))) {
                                    this.selector?.toggleRows(rows);
                                } else if (rows.some((r: any) => this.selector?.isRowSelected(r))) {
                                    const targetRows = rows.filter((r: any) => this.selector?.isRowSelected(r) !== true);
                                    targetRows.forEach((r: any) => this.selector?.select(r))
                                } else {
                                    this.selector?.toggleRows(rows);
                                }
                            }
                        }
                    } else {
                        var htInfo = this.flexGrid.hitTest(e);
                        if (htInfo.cellType == wjcGrid.CellType.Cell) {
                            // Row
                            // let row = this._rows[wjcCore.asNumber(r, false, true)] as Row
                            let row: wjcGrid.Row = this.flexGrid.rows[htInfo.row];
                            // Column
                            // col = this._cols[wjcCore.asNumber(c, false, true)] as Column;
                            let column: wjcGrid.Column = this.flexGrid.columns[htInfo.col];
                            let cellData = this.flexGrid.getCellData(htInfo.row, htInfo.col, false);
                            // onClick
                            this.selector?.select(row);
                        }
                    }
                }



            });
        }

        // click event
        if (this.onClick) {
            this.flexGrid.hostElement.addEventListener('click', (e) => {
                var htInfo = this.flexGrid.hitTest(e);
                if (htInfo.cellType == wjcGrid.CellType.Cell) {
                    // Row
                    // let row = this._rows[wjcCore.asNumber(r, false, true)] as Row
                    let row: wjcGrid.Row = this.flexGrid.rows[htInfo.row];
                    // Column
                    // col = this._cols[wjcCore.asNumber(c, false, true)] as Column;
                    let column: wjcGrid.Column = this.flexGrid.columns[htInfo.col];
                    let cellData = this.flexGrid.getCellData(htInfo.row, htInfo.col, false);
                    // onClick
                    this.onClick!(row.dataItem, cellData, row, column, htInfo);
                }
            });
        }

        // 더블 클릭 이벤트
        if (this.onDoubleClick) {
            this.flexGrid.hostElement.addEventListener('dblclick', (e) => {
                var htInfo = this.flexGrid.hitTest(e);
                if (htInfo.cellType == wjcGrid.CellType.Cell) {
                    // Row
                    // let row = this._rows[wjcCore.asNumber(r, false, true)] as Row
                    let row: wjcGrid.Row = this.flexGrid.rows[htInfo.row];
                    // Column
                    // col = this._cols[wjcCore.asNumber(c, false, true)] as Column;
                    let column: wjcGrid.Column = this.flexGrid.columns[htInfo.col];
                    let cellData = this.flexGrid.getCellData(htInfo.row, htInfo.col, false);
                    this.onDoubleClick!(row.dataItem, cellData, row, column, htInfo);
                }
            })
        }

        // 해당 브라우져에서는 컬럼 고정을 모두 해제
        // 모바일 브라우저에서 frozenColumns 에 해당하는 버튼이 클릭 이벤트가 발생하지 않음.
        if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)) {
            this.flexGrid.frozenColumns = 0;
        }

        applyNpEditStyles(this.flexGrid);
        applyNpSortingColumn(this.flexGrid, this);

        // itemSource는 일반적으로 CollectionView 이다.
        // 최초 CollecitonView를 선언하는 경우는 itemSourceChanged가 일어나지 않는다.
        // 이후 sourceCollection = data 등으로 선언하는 경우에도  itemSourceChanged가 아니다.
        // CollectionView를 아예 다시 넣거나 하는 경우에 itemSourceChanged가 발생한다.

        // 최초 초기화
        this.flexGrid.collectionView.collectionChanged.addHandler(this._onCollectionChanged);

        // 아이템 소스 자체가 변경될 시 다시 컬렉션뷰의 컬렉션 변경 이벤트를 등록해 주어야 한다.
        this.flexGrid.itemsSourceChanged.addHandler((cv?: any, e?: any, c?: any) => {
            this.debug("itemsSourceChanged");
            this.selector?.init();
            if (this.flexGrid.collectionView) {
                // 최초 한번 수행해 주어야 하는가 고민 필요
                // this._onCollectionChanged();
                this.flexGrid.collectionView.collectionChanged.addHandler(this._onCollectionChanged);
            }
        });


    }

    /**
     * 컬렉션 변경 시
     */
    private _onCollectionChanged = (cv?: any, e?: any, c?: any) => {

        if (!e || !e.action) {
            return;
        }

        // 셀렉터 재검증
        if (this._selector) {
            this._selector.revalidateSelectedItems();
        }

        if (e.action == wjcCore.NotifyCollectionChangedAction.Reset) {
            // 데이터 소스가 완전히 새로 설정됨
            this.debug('Collection was reset.');
            // 데이터가 변경되었을 시 솔팅으로 인한것인지 데이터 조회로 인한것인지 구분이 필요
            // this._np_gridEventState.sortingColIdx가 undefined가 아니면 솔팅에 의한것

            if (cv instanceof NpServerCollectionView === true) {
                // ServerCollectionView인 경우 currentItem 초기화
                setTimeout(() => { this.flexGrid.select(-1, -1) }, 1);
            } else if (this._np_gridEventState.sortingColIdx) {
                this._np_gridEventState.sortingColIdx = undefined;
            } else {
                // sorting에 의한 것이 아니면 초기화
                setTimeout(() => {

                    // collectionview를 초기화 하는것이 정석인듯 하나
                    // 그리드는 이와 별개로 또 내부적으로 selected 컬럼을 가지고 있는걸 발견함.
                    // 따라서 그리드의 선택을 초기화 함
                    // cv.currentItem = null;
                    this.flexGrid.select(-1, -1);

                }, 1);
            }
            this._np_gridEventState.sortingColIdx = undefined;
        } else if (e.action == wjcCore.NotifyCollectionChangedAction.Change) {
            // 데이터 아이템의 변경
            this.debug('Items changed.');
        } else if (e.action == wjcCore.NotifyCollectionChangedAction.Add) {
            // 데이터 아이템의 추가
            this.debug('Items added.');
        } else if (e.action == wjcCore.NotifyCollectionChangedAction.Remove) {
            // 데이터 아이템의 삭제
            this.debug('Items removed.');
        }

    }

    public get rowCount() {
        if (this.flexGrid) {
            return this.flexGrid.rows.length;
        } else {
            return 0;
        }
    }

    // selector properties
    public get selectedRowCount() {
        if (!this.selector) {
            return 0;
        }
        return this.selector.selectedRowCount;
    }

    public get selectedRows() {
        if (!this.selector) {
            return [];
        }
        return this.selector.selectedRows;
    }

    public get selectedItems() {
        if (!this.selector) {
            return [];
        }
        return this.selector.selectedItems;
    }


    /**
     * cv의 현재 item을 구한다.
     */
    public get currentItem(): any {
        if (!this.flexGrid || !this.flexGrid.collectionView || !this.flexGrid.collectionView.currentItem) {
            return null;
        }
        return this.flexGrid.collectionView.currentItem;
    }

    /**
     * flexGrid의 선택된 영역의 items를 구한다.
     */
    public get currentItems(): any[] {
        return this.flexGrid.selectedItems;
    }

    /**
     * flexGrid의 선택된 영역의 rows를 구한다.
     */
    public get currentRows(): any[] {
        if (this.flexGrid?.selectedRows && this.flexGrid.selectedRows.length > 0) {
            return this.flexGrid.selectedRows;
        } else {
            return [];
        }
    }
    //
    setCurrentRow(rowIdx: number) {
        this.flexGrid.select(new CellRange(rowIdx, 0, rowIdx, this.flexGrid.columns.length - 1), true);
    }

}

/**
 * 그리드에 edit 관련 스타일 적용
 * 
 * @param flexGrid 
 */
function applyNpEditStyles(flexGrid: FlexGrid) {

    // 편집중인 쉘이 아닌 같은 쉘의 다른 셀을 클릭해도 즉시 반영
    flexGrid.cellEditEnded.addHandler((s, e) => {
        let r = e.row;
        let c = e.col;
        flexGrid.itemsSource.commitEdit();
        s.select(r, c);
    });
    flexGrid.pastedCell.addHandler((s, e) => {
        let r = e.row;
        let c = e.col;
        flexGrid.itemsSource.commitEdit();
        s.select(r, c);
    });
    flexGrid.cellEditEnded.addHandler((s, e) => {
        const item = s.rows[e.row].dataItem;
        const col = s.columns[e.col];
        // 셀의 내용이 비어 있는지 확인
        if (col.binding && (item[col.binding] === null || item[col.binding] === '')) {
            flexGrid.itemsSource.commitEdit();
            // 수동으로 collectionChanged 이벤트 트리거
            s.collectionView.collectionChanged.raise(s, e);
        }
    });

    // formatHander(e, column, e.panel.cellType, cellTypeName, e.cell, binding, dataItem);

    NpFlexGridUtils.addFormatItemHandler2(flexGrid, (column, cellType, cellTypeStr, cellEl, binding, dataItem, g, e) => {

        if (g.isReadOnly === false) {
            if (cellTypeStr === 'ColumnHeader' && !column.isReadOnly) {
                cellEl.classList.add('np-editable-column');
                // column.cssClass = 'np-editable-row-cell';
            }

            if (cellTypeStr === 'Cell' && !column.isReadOnly) {
                cellEl.classList.add('np-editable-row-cell');
            }
        }

        if (g.itemsSource && g.itemsSource.trackChanges) {
            if (cellTypeStr === 'Cell') {
                let itemsSource: any = g.itemsSource;
                if (itemsSource.itemsEdited.length) {
                    if (itemsSource.itemsEdited.indexOf(dataItem) !== -1) {
                        e.cell.classList.add('np-edited-row-cell')
                        // toggleClass(e.cell, 'edited-row');
                    } else {
                        // toggleClass(e.cell, 'edited-row');
                        e.cell.classList.remove('np-edited-row-cell')
                    }
                }
            }
        }

        // if (cellTypeStr === 'Cell' && column.isReadOnly !== true) {
        //     cellEl.classList.add('np-editable-row-cell');
        // }

    });

    // flexGrid.formatItem.addHandler(function (s, e) {
    //     if (s.isReadOnly === false) {
    //         const column: Column = s.columns[e.col];
    //         if (!column) return;
    //         if (!column.isReadOnly && e.panel.cellType === CellType.ColumnHeader) {
    //             e.cell.classList.add('np-editable-column');
    //         }
    //         if (s.itemsSource && s.itemsSource.trackChanges) {
    //             if (e.panel.cellType === CellType.Cell) {
    //                 const row: Row = s.rows[e.row];
    //                 let itemsSource: any = s.itemsSource;
    //                 if (itemsSource.itemsEdited.length) {
    //                     if (itemsSource.itemsEdited.indexOf(row.dataItem) !== -1) {
    //                         e.cell.classList.add('np-edited-row-cell')
    //                         // toggleClass(e.cell, 'edited-row');
    //                     } else {
    //                         // toggleClass(e.cell, 'edited-row');
    //                         e.cell.classList.remove('np-edited-row-cell')
    //                     }
    //                 }
    //             }
    //         }
    //     }
    // });
}

/**
 * 기본적인 wijmo column sorting 동작 재정의 (for 멀티 솔팅)
 * @param flexGrid 
 */
function applyNpSortingColumn(flexGrid: FlexGrid, ctrl: NpFlexGridCtrl) {

    flexGrid.onSortingColumn = (e: CellRangeEventArgs) => {
        const grid = e.panel.grid;
        const cv = e.panel.grid.collectionView;

        switch (grid.allowSorting) {
            case AllowSorting.None:
                return false;
            case AllowSorting.SingleColumn:
                return true;
        }

        let column = e.getColumn();

        let prop = column.sortMemberPath || column.binding || '';
        if (!prop) return false;

        ctrl._np_gridEventState.sortingColIdx = e.col;

        let cvSorts = cv.sortDescriptions;
        cvSorts.deferUpdate(() => {
            if (!e.data.ctrlKey) {
                // 일반 솔팅
                let sort = cvSorts.find(v => v.property === prop);
                if (sort) {
                    const currentIdx = cvSorts.indexOf(sort);
                    let reverse = new SortDescription(prop, !sort.ascending);
                    // 이미 해당 솔팅이 있는건 순서 반대로
                    cvSorts.splice(currentIdx, 1, reverse);
                } else {
                    // 다르면 초기화 후 솔팅을 새로 넣음
                    cv.sortDescriptions.clear();
                    cv.sortDescriptions.push(new SortDescription(prop, true));
                }
            } else {
                let sort = cvSorts.find(v => v.property === prop);
                if (sort) {
                    const idx = cvSorts.indexOf(sort);
                    cvSorts.splice(idx, 1);
                } else {
                    // 다르면 초기화 후 솔팅을 새로 넣음
                    cv.sortDescriptions.push(new SortDescription(prop, true));
                }
            }
        })
        grid.onSortedColumn(e);

        if (ctrl.options.sortWithAutoSize === true) {
            flexGrid.autoSizeColumns(e.col, e.col);
        }

        return false;
    }
}